import { Box, Button } from '@factorialco/deprecated-design-system'
import { AtsRejectionReasonItemFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useUpdateRejectionReason } from 'modules/ats/hooks/rejectionReasons/useUpdateRejectionReason'

interface Props {
  rejectionReason: AtsRejectionReasonItemFragment
  onClose: () => void
}

interface UpdateRejectionReasonFormValues {
  id: number
  reason: string
  decisionMaker: string
}

export const UpdateRejectionReasonModal = ({ rejectionReason, onClose }: Props) => {
  const { update } = useUpdateRejectionReason()

  const form = useForm<UpdateRejectionReasonFormValues>({
    defaultValues: {
      id: rejectionReason.id,
      reason: rejectionReason.reason,
      decisionMaker: rejectionReason.decisionMaker,
    },
    validate(data) {
      const errors: FormErrors<UpdateRejectionReasonFormValues> = {}

      if (
        data.reason == rejectionReason.reason &&
        data.decisionMaker == rejectionReason.decisionMaker
      ) {
        errors.reason = i18n.t('ats.rejection_reasons.settings.edit_reason.no_changes_error')
        errors.decisionMaker = i18n.t('ats.rejection_reasons.settings.edit_reason.no_changes_error')
      }
      if (!data.reason) {
        errors.reason = i18n.t('common.empty')
      }

      return errors
    },
    async onSubmit(data, { setErrors }) {
      const result = await update({
        ...data,
        reason: data.reason,
        decisionMaker: data.decisionMaker == 'candidate' ? 'candidate' : 'company',
      })

      const handled = handleResult(result, (formErrors) => {
        setErrors(formErrors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.rejection_reasons.settings.save.success'),
          time: 2000,
        })
        onClose()
      }

      return handled
    },
  })

  const decisionMakerOptions = [
    {
      label: i18n.t('ats.rejection_reasons.settings.customize_reason.decision_maker.company'),
      value: 'company',
    },
    {
      label: i18n.t('ats.rejection_reasons.settings.customize_reason.decision_maker.candidate'),
      value: 'candidate',
    },
  ]

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('ats.rejection_reasons.settings.customize_reason.title')}
      description={i18n.t('ats.rejection_reasons.settings.customize_reason.description')}
      onSubmit={() => form.handleSubmit()}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={form.isSubmitting} submit>
            {i18n.t('ats.rejection_reasons.settings.customize_reason.save')}
          </Button>
        </>
      }
    >
      <Box gap={'s8'}>
        <Select
          mandatory
          label={i18n.t('ats.rejection_reasons.settings.customize_reason.decision_maker.label')}
          options={decisionMakerOptions}
          value={form.values.decisionMaker}
          error={form.errors.decisionMaker}
          onChange={(decisionMaker) =>
            form.setValues({ ...form.values, decisionMaker: decisionMaker })
          }
        />
        <TextField
          label={i18n.t('ats.rejection_reasons.settings.customize_reason.reason')}
          value={form.values.reason}
          mandatory
          onChange={(reason) => form.setValues({ ...form.values, reason: reason })}
          error={form.errors.reason}
        />
      </Box>
    </Modal>
  )
}
